import {
  Button,
  ButtonRadioGroup,
  Field,
  FieldContext,
  Select,
  SelectSizeVariant,
  Switch,
} from '@payaca/ambrosia';
import { FC, useContext, useEffect, useMemo, useState } from 'react';

import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';

import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { useAccount } from '@/utils/storeHooks';
import ErrorBoundary from '@payaca/components/errorBoundary/ErrorBoundary';

import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { ScheduledEventsPermissions } from '@payaca/permissions/scheduledEvents/scheduled-events.permissions';
import UntitledIcon from '@payaca/untitled-icons';
import { assertUnreachable } from '@payaca/utilities/guards';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfISOWeek,
  endOfMonth,
  formatDate,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
} from 'date-fns';
import {
  ScheduleContext,
  ScheduleRangeUnit,
} from '../contextProviders/ScheduleContextProvider';
import { DatePickerPopover } from '../datePicker/DatePickerPopover';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';

type ScheduleRange = {
  startDate: Date;
  endDate: Date;
  rangeUnit: ScheduleRangeUnit;
};

const ScheduleFilters: FC = () => {
  const account = useAccount();
  const calendarRef = useContext(ScheduleContext).calendarRef;
  const { startAt, endAt } = useContext(ScheduleContext).requestData;

  const scheduleRangeUnit = useContext(ScheduleContext).scheduleRangeUnit;
  const displayType = useContext(ScheduleContext).displayType;
  const onUpdate = useContext(ScheduleContext).onUpdate;
  const { calendarType } = useContext(ScheduleContext).calendarViewData;
  const [displayStartAt, setDisplayStartAt] = useState(startAt);
  const [displayEndAt, setDisplayEndAt] = useState(endAt);

  const { focusedDate, setFocusedDate } = useContext(ScheduleContext);

  const { data: accountRegionPreferences } = useGetMyRegionPreferences();

  const onIncrementRange = (unitIncrement: number) => {
    switch (scheduleRangeUnit) {
      case 'day':
        setFocusedDate(endOfDay(addDays(focusedDate, unitIncrement)));
        break;
      case 'week':
        setFocusedDate(endOfISOWeek(addWeeks(focusedDate, unitIncrement)));
        break;
      case 'month':
        setFocusedDate(endOfMonth(addMonths(focusedDate, unitIncrement)));
        break;
      default:
        assertUnreachable(scheduleRangeUnit);
    }
  };

  useEffect(() => {
    let startAt;
    let endAt;

    switch (scheduleRangeUnit) {
      case 'day':
        startAt = addDays(
          startOfDay(focusedDate),
          displayType === 'calendar' ? -1 : 0
        );
        endAt = addDays(
          endOfDay(focusedDate),
          displayType === 'calendar' ? 1 : 0
        );

        setDisplayStartAt(startOfDay(focusedDate));
        setDisplayEndAt(endOfDay(focusedDate));
        break;
      case 'week':
        startAt = addWeeks(
          startOfISOWeek(focusedDate),
          displayType === 'calendar' ? -1 : 0
        );
        endAt = addWeeks(
          endOfISOWeek(focusedDate),
          displayType === 'calendar' ? 1 : 0
        );

        setDisplayStartAt(startOfISOWeek(focusedDate));
        setDisplayEndAt(endOfISOWeek(focusedDate));
        break;
      case 'month':
        startAt = addMonths(
          startOfMonth(focusedDate),
          displayType === 'calendar' ? -1 : 0
        );
        endAt = addMonths(
          endOfMonth(focusedDate),
          displayType === 'calendar' ? 1 : 0
        );

        setDisplayStartAt(startOfMonth(focusedDate));
        setDisplayEndAt(endOfMonth(focusedDate));
        break;
      default:
        assertUnreachable(scheduleRangeUnit);
    }

    onUpdate?.({
      startAt,
      endAt,
    });

    calendarRef?.current?.getApi()?.gotoDate(focusedDate);
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'calendarRef.current', and 'onUpdate'
  }, [focusedDate, scheduleRangeUnit, displayType]);

  const viewDateLabel = useMemo(() => {
    switch (scheduleRangeUnit) {
      case 'day': {
        const format = getInternationalDateFormatByLocale(
          DateFormats.TODAY,
          accountRegionPreferences?.locale
        );

        return formatDate(displayStartAt || new Date(), format);
      }
      case 'week': {
        const format = getInternationalDateFormatByLocale(
          DateFormats.SHORT,
          accountRegionPreferences?.locale
        );

        return `${formatDate(
          displayStartAt || new Date(),
          format
        )} - ${formatDate(displayEndAt || new Date(), format)}`;
      }
      default:
        return formatDate(displayStartAt || new Date(), 'MMMM yyyy');
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'accountRegionPreferences.locale'
  }, [scheduleRangeUnit, displayStartAt, displayEndAt]);

  const calendarView = useMemo(() => {
    switch (calendarType) {
      case 'grid':
        switch (scheduleRangeUnit) {
          case 'day':
            return 'timeGridDay';
          case 'week':
            return 'timeGridWeek';
          case 'month':
            return 'dayGridMonth';
        }
        break;
      case 'userResource':
        switch (scheduleRangeUnit) {
          case 'day':
            return 'resourceTimelineDay';
          case 'week':
            return 'resourceTimelineWeek';
          case 'month':
            return 'resourceTimelineMonth';
        }
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: displayType
  }, [calendarType, scheduleRangeUnit, displayType]);

  useEffect(() => {
    calendarRef?.current?.getApi()?.changeView(calendarView);
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has an unnecessary dependency: calendarRef.
  }, [calendarView, calendarRef?.current]);

  return (
    <div className="flex w-full flex-wrap items-start gap-2">
      <div className="shrink-1 mr-auto flex grow-0 gap-2">
        <DatePickerPopover
          onChange={(date) => {
            if (date) {
              setFocusedDate(date);
            }
          }}
          defaultDate={new Date()}
        >
          {({ setIsOpen }) => (
            <Button
              variant={'white'}
              onClick={() => setIsOpen(true)}
              size={'sm'}
            >
              Jump to date
            </Button>
          )}
        </DatePickerPopover>
        <Button
          variant={'white'}
          onClick={() => {
            setFocusedDate(new Date());
            calendarRef?.current?.getApi()?.today();
          }}
          size={'sm'}
        >
          Today
        </Button>
      </div>
      <div className="flex items-center gap-4">
        <Button
          variant={'white'}
          onClick={() => {
            onIncrementRange(-1);
            calendarRef?.current?.getApi()?.prev();
          }}
          size={'sm'}
        >
          <span className="sr-only">Previous period</span>
          <UntitledIcon name="chevron-left" className="h-5 w-5" />
        </Button>
        <span className="text-base">{viewDateLabel}</span>
        <Button
          variant={'white'}
          size={'sm'}
          onClick={() => {
            onIncrementRange(1);
            calendarRef?.current?.getApi()?.next();
          }}
        >
          <span className="sr-only">Next period</span>
          <UntitledIcon name="chevron-right" className="h-5 w-5" />
        </Button>
      </div>
      <div className="shrink-1 ml-auto w-[180px] grow-0">
        <ErrorBoundary>
          <Select
            sizeVariant={SelectSizeVariant.SM}
            value={scheduleRangeUnit}
            onChange={(scheduleRangeUnit) => {
              onUpdate({
                scheduleRangeUnit: scheduleRangeUnit as ScheduleRangeUnit,
              });
            }}
            options={[
              { label: 'Day', value: 'day' },
              { label: 'Week', value: 'week' },
              { label: 'Month', value: 'month' },
            ]}
          >
            {displayType === 'calendar' && (
              <>
                <hr className="border-gray-200" />
                <ExcludeNonBusinessHoursField />
              </>
            )}
          </Select>
        </ErrorBoundary>
      </div>
      {displayType === 'calendar' && (
        <PermissionGuard
          renderIfHasPermissions={[
            ScheduledEventsPermissions.GET_EVENTS,
            ScheduledEventsPermissions.GET_MY_DEAL_EVENTS,
          ]}
        >
          <PermissionGuard
            renderIfHasPermissions={[AccountsPermissions.GET_USERS]}
          >
            <div className="shrink-0 grow-0">
              <ButtonRadioGroup
                value={calendarType}
                onChange={(calendarType) => {
                  onUpdate({ calendarType });
                }}
                options={[
                  { label: 'Grid', value: 'grid' },
                  { label: 'User', value: 'userResource' },
                ]}
                sizeVariant={'sm'}
                CustomOption={({ option }) => {
                  return (
                    <UntitledIcon
                      className="h-6 w-6"
                      name={option.value == 'grid' ? 'calendar' : 'list'}
                    />
                  );
                }}
              />
            </div>
          </PermissionGuard>
        </PermissionGuard>
      )}
    </div>
  );
};

export default ScheduleFilters;

const ExcludeNonBusinessHoursField: FC = () => {
  const onUpdate = useContext(ScheduleContext).onUpdate;
  const { excludeNonBusinessHours } =
    useContext(ScheduleContext).calendarViewData;
  const { id } = useContext(FieldContext);

  return (
    <Field.Legacy>
      <div className="items-top flex gap-4 px-4 py-2">
        <div className="shrink-0">
          <Switch
            value={excludeNonBusinessHours}
            onChange={(value) => {
              onUpdate({
                excludeNonBusinessHours: value,
              });
            }}
          />
        </div>
        <label htmlFor={id}>Show only business hours</label>
      </div>
    </Field.Legacy>
  );
};
